import { Box, Button, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow, useTheme } from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { Device, EldVehicle, Equipment, MaintenanceItem, MaintenanceReminder } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import NoneFound from "src/components/none-found";
import Iconify from "src/components/iconify/iconify";
import { ColoredIcon } from "src/components/colored-icon";
import { ReminderIcon } from "../../reminders/list";

type MaintenanceRemindersProps = {
  maintenanceReminders?: MaintenanceReminder[],
}

const MaintenanceReminders = memo(({ maintenanceReminders }: MaintenanceRemindersProps) => {
  const { sortedList, sortData } = useSort(maintenanceReminders);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, [
    "deviceId",
    "deviceType",
    "vin",
    "odometer",
  ]);

  const theme = useTheme();

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Maintenance Reminders" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table size="small">
          <TableHead>
            <TableRow>

              <SortableHeader sortKey="intensity" sortData={sortData} >
                Level
              </SortableHeader>

              <SortableHeader sortKey="deviceId" sortData={sortData} >
                Device ID
              </SortableHeader>

              <SortableHeader sortKey="deviceType" sortData={sortData} >
                Device Type
              </SortableHeader>

              <SortableHeader sortKey="vin" sortData={sortData} >
                VIN
              </SortableHeader>

              <SortableHeader sortKey="odometer" sortData={sortData} >
                Odometer
              </SortableHeader>

              <TruxTableCell>
                Reason
              </TruxTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredList.map((maintenanceReminder, index) =>
            (<TableRow
              key={index}
            >
              <TruxTableCell>
                {maintenanceReminder.intensity !== "none" && <ReminderIcon intensity={maintenanceReminder.intensity} />}
              </TruxTableCell>

              <TruxTableCell>
                {maintenanceReminder.deviceId}
              </TruxTableCell>

              <TruxTableCell>
                {maintenanceReminder.deviceType}
              </TruxTableCell>

              <TruxTableCell>
                {maintenanceReminder.vin}
              </TruxTableCell>

              <TruxTableCell>
                {maintenanceReminder.odometer}
              </TruxTableCell>

              <TruxTableCell>
                {maintenanceReminder.items.map((item, idx) => {
                  let color: string;
                  if (item.intensity === "error") {
                    color = theme.palette.error.main;
                  } else if (item.intensity === "warning") {
                    color = "#ff9544";
                  } else {
                    color = theme.palette.success.main;
                  }

                  return (
                    <Box key={idx} color={color} sx={{ fontWeight: 600 }}>
                      {item.maintenanceItem.name} - {item.dueIn}
                    </Box>
                  );
                })}
              </TruxTableCell>

            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  maintenanceItems?: MaintenanceItem[]
  maintenanceReminders?: MaintenanceReminder[]
  devices?: Device[]
  eldVehicles?: EldVehicle[]
  equipment?: Equipment[]
}

export default function Page() {
  const settings = useSettingsContext();
  const dataLoader = useCallback(() => ({
    maintenanceItems: axios.get(endpoints.maintenanceItems.findAll).then(response => response.data),
    maintenanceReminders: axios.get(endpoints.maintenanceReminders.findAll).then(response => response.data),
    devices: axios.get(endpoints.devices.findAll).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
  }), []);
  const {
    data,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && <>
          {data?.devices?.length === 0 ? <MaintenanceRemindersNoneFound lines={["You don't have any Assets/Devices added.", "After you add Vehicles and Equipment you will be able to track their mantenance history on this page."]} /> : <>
            {data?.maintenanceItems?.length === 0 ? <MaintenanceRemindersNoneFound lines={["To see upcoming Maintenance Reminders please setup Maintenance Settings first.", "To open the Maintenance Settings click the button below."]} buttonLink={{href: "/app/maintenances/settings", text: "Maintenance Settings"}} /> : <>
              {data?.maintenanceReminders?.length === 0 ? <MaintenanceRemindersNoneFound lines={["It appears no Vehicles or Equipment have any Maintenances coming up in the next 2 weeks or in the next 14k miles.", "If you believe this is incorrect please reach out to Customer Support."]} /> : <>
                <PageHeader title="Maintenance Reminders" />
                <MaintenanceReminders maintenanceReminders={data.maintenanceReminders} />

              </>}
            </>}
          </>}
        </>}
      </DisplayPageData>
    </Container >
  );
}

function MaintenanceRemindersNoneFound({lines, buttonLink}: {lines: string[], buttonLink?: {href: string, text: string}}) {
  return <NoneFound title={<div>
    <Box textAlign="center">
      <ColoredIcon color="success">
        <Iconify icon="solar:check-circle-bold" width={44} />
      </ColoredIcon>
    </Box>
    No Maintenance Reminders Found
  </div>} message={<div>
    {lines.map((l, i) => <div key={i}>{l}</div>)}

    {buttonLink && <Box mt={2}>
      <Button variant="outlined" color="info" href={buttonLink.href}>{buttonLink.text}</Button>
    </Box>}
  </div>} />
}